/**
 * dishOptions: the rules for choosing a dish's options (sides, sizes,
 * extras), shared by the restaurant dish dialog and the Order together dish
 * dialog so both behave the same.
 *
 * `selected` maps an option group's id to the options chosen in it:
 *   { [groupId]: [option, ...] }
 */

/**
 * Tap an option. A chosen option is let go unless that would drop its group
 * below `minOptions`; a new one is added, and when the group is full
 * (`maxOptions`) it replaces the choice ("pick one drink").
 */
export const toggleDishOption = (selected, product, groupId, option) => {
  const current = selected[groupId] || [];
  const group = (product?.optionGroups || []).find((g) => g._id === groupId);
  const exists = current.some((item) => item._id === option._id);

  if (exists) {
    const minRequired = group?.minOptions || 0;
    if (minRequired > 0 && current.length <= minRequired) return selected;
    return { ...selected, [groupId]: current.filter((item) => item._id !== option._id) };
  }

  const max = group?.maxOptions;
  let updated = [...current, option];
  if (max && max > 0 && updated.length > max) updated = [option];
  return { ...selected, [groupId]: updated };
};

/* Every group's minimum is met. */
export const dishOptionsComplete = (product, selected) => {
  if (!product?.optionGroups?.length) return true;
  return product.optionGroups.every((group) => {
    if (!group.minOptions) return true;
    return (selected[group._id]?.length || 0) >= group.minOptions;
  });
};

/* The chosen options in one list, as a cart line or basket line carries them. */
export const chosenDishOptions = (selected) => Object.values(selected || {}).flat().filter(Boolean);

/* Choices rebuilt from option ids (a line already in a cart or basket). */
export const dishSelectionFromOptions = (product, options) => {
  const ids = new Set(
    (options || []).map((option) => String(option?._id || option || "")).filter(Boolean)
  );
  if (!ids.size || !product?.optionGroups?.length) return {};
  return product.optionGroups.reduce((acc, group) => {
    const matches = (group.options || []).filter((option) => ids.has(String(option._id)));
    if (matches.length > 0) acc[group._id] = matches;
    return acc;
  }, {});
};
